import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Layout from "./components/Layout";
import SessionTimeoutManager from "./components/SessionTimeoutManager";
import Login from "./pages/Login";
import SalesDashboard from "./pages/SalesDashboard";
import CollectionsDashboard from "./pages/CollectionsDashboard";
import AdminUsers from "./pages/AdminUsers";
import AdminAudit from "./pages/AdminAudit";
import ActivityReports from "./pages/ActivityReports";
import Reports from "./pages/Reports";
import CRMQueue from "./pages/CRMQueue";
import SmsNeedsAttention from "./pages/SmsNeedsAttention";
import PatientScheduling from "./pages/PatientScheduling";
import ReferralIntake from "./pages/ReferralIntake";
import ManualReferralEntry from "./pages/ManualReferralEntry";
import PatientSearch from "./pages/PatientSearch";
import BugTracker from "./pages/BugTracker";
import NetworkScheduling from "./pages/NetworkScheduling";
import ClinicSchedulePage from "./pages/ClinicSchedulePage"; // P7 (dark: the server decides who sees it)
import ChangePassword from "./pages/ChangePassword";
import MfaEnroll from "./pages/MfaEnroll";
import BillingHome from "./pages/BillingHome";
import BillingRemit from "./pages/BillingRemit";
import BillingPI from "./pages/BillingPI";
import BillingWC from "./pages/BillingWC";
import BillingCase from "./pages/BillingCase";
import BillingWCFollowup from "./pages/BillingWCFollowup";
import BillingIntake from "./pages/BillingIntake";
import BillingMoneyIn from "./pages/BillingMoneyIn";
// External Portal pages
import ClientDashboard from "./pages/portal/ClientDashboard";
import PatientList from "./pages/portal/PatientList";
import PatientDetail from "./pages/portal/PatientDetail";
import Requests from "./pages/portal/Requests";
import Settings from "./pages/portal/Settings";
import PartnerPatients from "./pages/partner/PartnerPatients";
import PartnerPatientDetail from "./pages/partner/PartnerPatientDetail";
import PartnerShell from "./pages/partner/PartnerShell";
import ClinicPortal from "./pages/ClinicPortal";
import ClinicSettings from "./pages/ClinicSettings";
import ClinicScheduling from "./pages/ClinicScheduling";
import ClinicPendingRequests from "./pages/ClinicPendingRequests";
import ClinicFollowUps from "./pages/ClinicFollowUps";
import { Toaster } from "sonner";
import "./App.css";

function ProtectedRoute({ children, allowedRoles, requireExternal, requireClinic, requirePartner }) {
  const { user, loading, isExternalUser, isClinicUser, isPartnerUser } = useAuth();
  if (loading) return <div className="flex h-screen items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  // Force password change before accessing any other page
  if (user.must_change_password && window.location.pathname !== "/change-password")
    return <Navigate to="/change-password" replace />;
  // CLINIC users must use /clinic; any attempt to enter /portal or internal
  // routes bounces them to their own portal.
  // Partner users (2026-10) live only in /partner; everyone else is kept out.
  if (isPartnerUser && !requirePartner)
    return <Navigate to="/partner" replace />;
  if (requirePartner && !isPartnerUser)
    return <Navigate to="/" replace />;
  if (isClinicUser && !requireClinic)
    return <Navigate to="/clinic/scheduling" replace />;
  if (requireClinic && !isClinicUser)
    return <Navigate to="/" replace />;
  // Check if route requires external user (CLIENT-style law-firm portal)
  if (requireExternal && !isExternalUser)
    return <Navigate to="/" replace />;
  // Check if route requires internal user with specific roles
  if (allowedRoles && !allowedRoles.includes(user.role))
    return <Navigate to="/" replace />;
  return children;
}

function DefaultRedirect() {
  const { user, isExternalUser, isClinicUser, isPartnerUser } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;

  if (isPartnerUser) return <Navigate to="/partner" replace />;

  if (isClinicUser) return <Navigate to="/clinic/scheduling" replace />;
  // External users go to client portal
  if (isExternalUser) return <Navigate to="/portal/dashboard" replace />;
  
  // Internal users go to their dashboards
  if (user.role === "sales") return <Navigate to="/dashboard/sales" replace />;
  if (user.role === "collections") return <Navigate to="/dashboard/collections" replace />;
  if (user.role === "location") return <Navigate to="/crm-queue" replace />;
  return <Navigate to="/dashboard/sales" replace />;
}

function App() {
  return (
    <AuthProvider>
      <SessionTimeoutManager>
        <Toaster richColors position="top-right" />
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            {/* PUBLIC patient availability page (Contract §11, scheduling
                Commit 3): a sibling of /login, deliberately OUTSIDE the `/`
                Layout route — no sidebar, no nav, no staff chrome, no
                ProtectedRoute. Reachable only with a valid token. */}
            <Route path="/schedule/:purpose" element={<PatientScheduling />} />
            <Route path="/change-password" element={<ChangePassword />} />
          
          {/* Internal Portal Routes (wrapped in Layout) */}
          <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            <Route index element={<DefaultRedirect />} />
            <Route
              path="dashboard/sales"
              element={
                <ProtectedRoute allowedRoles={["sales", "admin"]}>
                  <SalesDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="dashboard/collections"
              element={
                <ProtectedRoute allowedRoles={["collections", "admin"]}>
                  <CollectionsDashboard />
                </ProtectedRoute>
              }
            />
            <Route path="reports" element={<ProtectedRoute allowedRoles={["admin"]}><ActivityReports /></ProtectedRoute>} />
            <Route path="referral-reports" element={<ProtectedRoute allowedRoles={["admin", "sales"]}><Reports /></ProtectedRoute>} />
            <Route path="admin/users" element={<ProtectedRoute allowedRoles={["admin"]}><AdminUsers /></ProtectedRoute>} />
            <Route path="admin/audit" element={<ProtectedRoute allowedRoles={["admin"]}><AdminAudit /></ProtectedRoute>} />
            <Route path="crm-queue" element={<ProtectedRoute allowedRoles={["sales", "admin", "location"]}><CRMQueue /></ProtectedRoute>} />
            {/* SMS needs attention (Contract §7) — staff-wide, NOT
                location-scoped: a failed text is WestStar's problem, not a
                site's, so the `location` role is deliberately excluded. */}
            <Route path="sms-needs-attention" element={<ProtectedRoute allowedRoles={["sales", "admin"]}><SmsNeedsAttention /></ProtectedRoute>} />
            <Route path="referral-intake" element={<ProtectedRoute allowedRoles={["sales", "admin"]}><ReferralIntake /></ProtectedRoute>} />
            <Route path="manual-referral" element={<ProtectedRoute allowedRoles={["sales", "admin", "location"]}><ManualReferralEntry /></ProtectedRoute>} />
            <Route path="patient-search" element={<ProtectedRoute allowedRoles={["sales", "admin"]}><PatientSearch /></ProtectedRoute>} />
            <Route path="network-scheduling" element={<ProtectedRoute allowedRoles={["sales", "admin"]}><NetworkScheduling /></ProtectedRoute>} />
            <Route path="clinic-schedule" element={<ProtectedRoute allowedRoles={["sales", "admin", "location"]}><ClinicSchedulePage /></ProtectedRoute>} />
            <Route path="bug-tracker" element={<ProtectedRoute allowedRoles={["sales", "admin", "collections"]}><BugTracker /></ProtectedRoute>} />
            {/* Billing (2026-10-25): dark - the page asks the server's billing gate and goes home unless it says yes */}
            <Route path="billing" element={<ProtectedRoute><BillingHome /></ProtectedRoute>} />
            {/* Money In list (PR #72 update, 2026-11-01): the EOBs the bot read - moved off the Billing home */}
            <Route path="billing/eob" element={<ProtectedRoute><BillingMoneyIn /></ProtectedRoute>} />
            {/* Money In (2026-10-26): one EOB's review page, behind the same server answer */}
            <Route path="billing/eob/:remitId" element={<ProtectedRoute><BillingRemit /></ProtectedRoute>} />
            {/* PI liens (2026-10-28): the workqueue, one firm's or the no-attorney cases, one case - same server answer */}
            <Route path="billing/pi" element={<ProtectedRoute><BillingPI /></ProtectedRoute>} />
            <Route path="billing/pi/firm/:payorId" element={<ProtectedRoute><BillingPI /></ProtectedRoute>} />
            <Route path="billing/pi/no-attorney" element={<ProtectedRoute><BillingPI /></ProtectedRoute>} />
            <Route path="billing/pi/case/:caseId" element={<ProtectedRoute><BillingPI /></ProtectedRoute>} />
            {/* WC claim readiness (2026-10-29): the checks, one check's claims - same server answer */}
            <Route path="billing/wc" element={<ProtectedRoute><BillingWC /></ProtectedRoute>} />
            <Route path="billing/wc/check/:check" element={<ProtectedRoute><BillingWC /></ProtectedRoute>} />
            {/* Billing PR #6 (2026-10-30): any case, WC follow-up by carrier - dark like the rest */}
            <Route path="billing/case/:caseId" element={<ProtectedRoute><BillingCase /></ProtectedRoute>} />
            <Route path="billing/wc/followup" element={<ProtectedRoute><BillingWCFollowup /></ProtectedRoute>} />
            <Route path="billing/wc/followup/carrier/:payorId" element={<ProtectedRoute><BillingWCFollowup /></ProtectedRoute>} />
            <Route path="billing/wc/followup/no-carrier" element={<ProtectedRoute><BillingWCFollowup /></ProtectedRoute>} />
            {/* PR #7 (2026-10-30): network charge intake - upload a clinic batch, review the proposed lines */}
            <Route path="billing/intake" element={<ProtectedRoute><BillingIntake /></ProtectedRoute>} />
            <Route path="billing/intake/:batchId" element={<ProtectedRoute><BillingIntake /></ProtectedRoute>} />
            {/* Opt-in MFA enrollment. Reached only by a deliberate user
                action — no force, no auto-redirect. */}
            <Route path="account/mfa-setup" element={<ProtectedRoute><MfaEnroll /></ProtectedRoute>} />
          </Route>
          
          {/* External Portal Routes (each has its own sidebar via PortalSidebar component) */}
          <Route
            path="/portal/dashboard"
            element={
              <ProtectedRoute requireExternal>
                <ClientDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/portal/patients"
            element={
              <ProtectedRoute requireExternal>
                <PatientList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/portal/patients/:id"
            element={
              <ProtectedRoute requireExternal>
                <PatientDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/portal/requests"
            element={
              <ProtectedRoute requireExternal>
                <Requests />
              </ProtectedRoute>
            }
          />
          <Route
            path="/portal/settings"
            element={
              <ProtectedRoute requireExternal>
                <Settings />
              </ProtectedRoute>
            }
          />

          {/* Partner view (2026-10) — read-only, own shell, dark behind
              PARTNER_VIEW_TENANTS on the server */}
          <Route
            path="/partner"
            element={
              <ProtectedRoute requirePartner>
                <PartnerPatients />
              </ProtectedRoute>
            }
          />
          {/* 2-step setup for partner accounts (opt-in, as for any outside
              user): the same MfaEnroll page, inside the partner shell, so
              the partner-only redirect doesn't block it. */}
          <Route
            path="/partner/2-step"
            element={
              <ProtectedRoute requirePartner>
                <PartnerShell>
                  <MfaEnroll />
                </PartnerShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/partner/patients/:ref"
            element={
              <ProtectedRoute requirePartner>
                <PartnerPatientDetail />
              </ProtectedRoute>
            }
          />

          {/* Clinic Portal Routes (Phase 1C) — own shell, isolated from /portal */}
          <Route
            path="/clinic"
            element={
              <ProtectedRoute requireClinic>
                <ClinicPortal />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/clinic/scheduling" replace />} />
            <Route path="scheduling" element={<ClinicScheduling />} />
            <Route path="pending" element={<ClinicPendingRequests />} />
            <Route path="follow-ups" element={<ClinicFollowUps />} />
            <Route path="settings" element={<ClinicSettings />} />
          </Route>
        </Routes>
      </BrowserRouter>
      </SessionTimeoutManager>
    </AuthProvider>
  );
}

export default App;
